const session = useSessionStorage()
const longterm = useLongtermStorage()
const deliveryCountries = useDeliveryCountries()
const deliveryStates = useDeliveryStates()
const authentication = useAuthentication()
const api = useApi()
const tracking = useTracking()

if (!session.orders) {
  session.orders = {}
}

export default (
  deal: Deal,
  createRemoteOrderForStep: null | 'order' | 'details' = null,
  shouldClearLocalOrder: boolean = false,
): LocalOrder => {
  // create a local order in the session:
  // - if we don't have one
  // - if it is marked for deletion
  // - if the user has changed AND the order was not done by an unahtuenticated user

  if (
    !session.orders[deal.id] ||
    shouldClearLocalOrder ||
    (session.orders[deal.id].user?.id !== null &&
      session.orders[deal.id].user?.id !== authentication.getUser().id)
  ) {
    // create an empty order
    session.orders[deal.id] = {
      items: {},
      itemData: {},
      itemTimestamps: {},
      user: authentication.getUser(),
      deliveryAddress: longterm.autocomplete?.deliveryAddress ?? {
        street: '',
        number: '',
        building: '',
        zip: '',
        city: '',
        state: deliveryStates[0] ?? '',
        country: deliveryCountries[0] ?? '',
      },
      deliveryServicePoint: null,
      invoiceAddress: longterm.autocomplete?.invoiceAddress ?? {
        company_name: '',
        tax_id: '',
        phone: '',
        street: '',
        number: '',
        building: '',
        zip: '',
        city: '',
        state: deliveryStates[0] ?? '',
        country: deliveryCountries[0] ?? '',
      },
      invoiceRequired: longterm.autocomplete?.invoiceRequired ?? false,
      invoiceReason: longterm.autocomplete?.invoiceReason ?? false,
      extraFieldValues: {},
      paymentMethodIdentifier: deal.paymentMethods[0]?.identifier ?? null,
      shouldCreateMandate: false,
      remoteOrder: null,
      overwriteOrderId: null,
      coupons: [],
      taxes: [],
    }

    // add fees
    incrementInitialOption()
  }

  // if the user is authenticated, update the user, but keep the missing data from the session
  if (authentication.isAuthenticated()) {
    const authenticatedUser = authentication.getUser()

    authenticatedUser.firstName =
      authenticatedUser.firstName || session.orders[deal.id].user.firstName
    authenticatedUser.lastName =
      authenticatedUser.lastName || session.orders[deal.id].user.lastName
    authenticatedUser.birthDate =
      authenticatedUser.birthDate || session.orders[deal.id].user.birthDate
    authenticatedUser.phone =
      authenticatedUser.phone || session.orders[deal.id].user.phone

    session.orders[deal.id].user = authenticatedUser
  }

  // create a remote order for logging
  if (
    createRemoteOrderForStep !== null &&
    !session.orders[deal.id].remoteOrder
  ) {
    createRemoteOrder()
  }

  // get and set the quantity of an option
  function setOptionQuantity(option: Option, quantity: number) {
    if (
      quantity > 0 &&
      !getAllowedOptionQuantities(option).includes(quantity) &&
      !option.isFee &&
      !(option.isExtra && option.isSingular)
    ) {
      return
    }

    if (!session.orders[deal.id].items[option.id]) {
      session.orders[deal.id].itemTimestamps[option.id] = new Date().getTime()
    }

    clearTaxes()

    session.orders[deal.id].items[option.id] = quantity
  }

  function getOptionQuantity(option: Option): number {
    return session.orders[deal.id].items[option.id] ?? 0
  }

  // get the next possible quantity
  function getNextOptionQuantity(option: Option, direction: number): number {
    if (option.isFee) {
      return 1
    }

    const allowedOptionQuantities = getAllowedOptionQuantities(option)
    const index = Math.min(
      Math.max(
        0,
        allowedOptionQuantities.indexOf(getOptionQuantity(option)) + direction,
      ),
      allowedOptionQuantities.length - 1,
    )

    //@ts-ignore
    return allowedOptionQuantities[index] ?? 0
  }

  // increment & decrement
  function incrementInitialOption(option?: Option) {
    session.orders[deal.id].items = {}
    session.orders[deal.id].itemTimestamps = {}

    // add all fees
    deal.fees.forEach((fee: Option) => {
      incrementOption(fee)
    })

    // if there is only one variant with only one option, auto select it
    if (
      !option &&
      deal.variants.length === 1 &&
      deal.variants[0].options.length === 1
    ) {
      incrementOption(deal.variants[0].options[0])
    } else if (option) {
      incrementOption(option)
    }
  }

  function incrementOption(option: Option) {
    setOptionQuantity(option, getNextOptionQuantity(option, 1))
    tracking.trackEvent('salesflow_partner_add_amount')
  }

  function setOptionData(option: Option, data: any) {
    session.orders[deal.id].itemData[option.id] = data
  }

  function getOptionData(option: Option, defaultData: any = null): any {
    return session.orders[deal.id].itemData[option.id] ?? defaultData
  }

  function decrementOption(option: Option) {
    setOptionQuantity(option, getNextOptionQuantity(option, -1))
    tracking.trackEvent('salesflow_partner_remove_amount')
  }

  // check if an option is selected
  function isOptionSelected(option: Option): boolean {
    return (session.orders[deal.id].items[option.id] ?? 0) > 0
  }

  // check if all options of a variant are selected
  function isVariantSelected(variant: Variant): boolean {
    let isSelected = true

    variant.options.forEach((option: Option) => {
      isSelected = isSelected && isOptionSelected(option)
    })

    return isSelected
  }

  // all options
  const allOptions: ComputedRef<{ [key: number]: Option }> = computed(() => {
    const options: { [key: number]: Option } = {}

    ;[...deal.variants, ...deal.extraVariants].forEach((variant: Variant) => {
      variant.options.forEach((option: Option) => {
        options[option.id] = option
      })
    })

    deal.fees.forEach((option: Option) => {
      options[option.id] = option
    })

    return options
  })

  // selected options
  const allSelectedOptions = computed(() => {
    const selectedOptions = []

    for (let optionId in session.orders[deal.id].items) {
      const optionQuantity = session.orders[deal.id].items[optionId] ?? 0
      const option = allOptions.value[parseInt(optionId)] ?? null

      if (
        option.isFee /*|| (!option.isFee && option.isExtra && option.isSingular)*/ ||
        (optionQuantity > 0 &&
          (isTest() || option.stock === null || option.stock >= optionQuantity))
      ) {
        selectedOptions.push({
          option: option,
          quantity: optionQuantity,
        })
      }
    }

    selectedOptions.sort((a: OptionQuantity, b: OptionQuantity) => {
      return (
        (session.orders[deal.id].itemTimestamps[b.option.id] ?? 0) -
        (session.orders[deal.id].itemTimestamps[a.option.id] ?? 0)
      )
    })

    return selectedOptions
  })

  const selectedFeeOptions = computed(() => {
    return allSelectedOptions.value.filter(({ option }) => option.isFee)
  })

  const selectedRegularOptions = computed(() => {
    return allSelectedOptions.value.filter(
      ({ option }) => !option.isFee && !(option.isExtra && option.isSingular),
    )
  })

  const selectedSingularOptions = computed(() => {
    return allSelectedOptions.value.filter(
      ({ option }) => !option.isFee && option.isExtra && option.isSingular,
    )
  })

  const hasValidOptionSelection = computed(() => {
    return (
      // Regular options
      selectedRegularOptions.value.filter(
        (optionQuantity: OptionQuantity) => !optionQuantity.option.isExtra,
      ).length > 0 &&
      // Check all option data, if any of them has openGdsData and isValid === false, then the order is invalid
      selectedRegularOptions.value.filter(
        (optionQuantity: OptionQuantity) =>
          getOptionData(optionQuantity.option, null)?.openGdsData?.isValid ===
          false,
      ).length === 0
    )
  })

  const hasOpenGdsOptions = computed(() => {
    return selectedRegularOptions.value.some(
      (optionQuantity: OptionQuantity) =>
        optionQuantity.option.openGdsRateId !== null,
    )
  })

  const hasValidSingularOptionSelection = computed(() => {
    return (
      selectedSingularOptions.value.filter(
        (optionQuantity: OptionQuantity) =>
          optionQuantity.option.isExtra && optionQuantity.option.isSingular,
      ).length > 0
    )
  })

  const shouldApplyFees = computed(() => {
    return (
      !(authentication.getUser().isPlus ?? false) &&
      !(authentication.getUser().isCombinedPurchase ?? false)
    )
  })

  // totals (only calculate when the order is valid)

  function getOptionDiscountPrice(option: Option): number {
    // Allow for the option price to be overridden locally by setting the optionData with a discountPrice
    return parseFloat(
      getOptionData(option)?.discountPrice ?? option.discountPrice,
    )
  }

  function getOptionOriginalPrice(option: Option): number {
    // Allow for the option price to be overridden locally by setting the optionData with a originalPrice
    return parseFloat(
      getOptionData(option)?.originalPrice ?? option.originalPrice,
    )
  }

  const totalFeeOptions = computed(() => {
    if (!hasValidOptionSelection.value) {
      return 0
    }

    return selectedFeeOptions.value.reduce(
      (total, { option, quantity }) =>
        (total += getOptionDiscountPrice(option) * quantity),
      0,
    )
  })

  const totalRegularOptionsWithDiscount = computed(() => {
    if (!hasValidOptionSelection.value) {
      return 0
    }

    return selectedRegularOptions.value.reduce(
      (total, { option, quantity }) =>
        (total += getOptionDiscountPrice(option) * quantity),
      0,
    )
  })

  const totalSingularOptionsWithDiscount = computed(() => {
    if (!hasValidSingularOptionSelection.value) {
      return 0
    }

    return selectedSingularOptions.value.reduce(
      (total, { option, quantity }) =>
        (total += parseFloat(option.discountPrice) * quantity),
      0,
    )
  })
  const totalRegularOptionsOriginal = computed(() => {
    if (!hasValidOptionSelection.value) {
      return 0
    }

    return selectedRegularOptions.value.reduce(
      (total, { option, quantity }) =>
        (total += getOptionOriginalPrice(option) * quantity),
      0,
    )
  })

  const totalPayable = computed(() => {
    if (!hasValidOptionSelection.value) {
      return 0
    }

    return (
      totalRegularOptionsWithDiscount.value +
      totalSingularOptionsWithDiscount.value +
      (shouldApplyFees.value ? totalFeeOptions.value : 0) +
      totalTaxes.value
    )
  })

  const totalDiscount = computed(() => {
    if (!hasValidOptionSelection.value) {
      return 0
    }

    return (
      totalRegularOptionsOriginal.value - totalRegularOptionsWithDiscount.value
    )
  })

  const totalCoupons = computed(() => {
    return coupons.value.reduce(
      (total: number, coupon: Coupon) =>
        (total += coupon.calculatedAmount ?? 0),
      0,
    )
  })

  const totalPayableWithCoupons = computed(() => {
    return (
      Math.round(Math.max(0, totalPayable.value - totalCoupons.value) * 100) /
      100
    )
  })

  const totalTaxes = computed(() => {
    if (!hasValidOptionSelection.value) {
      return 0
    }

    return taxes.value.reduce(
      (total: number, tax: RemoteOrderDetail) =>
        (total += parseFloat(tax.value)),
      0,
    )
  })

  // does this order need shipping?

  const needsShipping = computed(() => {
    return (deal.checkoutFields.street ?? null) !== null
  })

  // taxes
  function clearTaxes(): void {
    session.orders[deal.id].taxes = []
  }

  const taxes = computed(() => {
    return session.orders[deal.id].taxes ?? []
  })

  const needsTaxes = computed(() => {
    if (isRegionUS()) {
      return taxes.value.length === 0
    }

    return false
  })

  // details

  const user = computed({
    get: () => session.orders[deal.id].user,
    set(value: User) {
      session.orders[deal.id].user = value
    },
  })

  const deliveryAddress = computed({
    get: () => session.orders[deal.id].deliveryAddress,
    set(value: Address) {
      session.orders[deal.id].deliveryAddress = value
    },
  })

  const deliveryServicePoint = computed({
    get: () => session.orders[deal.id].deliveryServicePoint,
    set(value: SendCloudServicePoint | null) {
      session.orders[deal.id].deliveryServicePoint = value
    },
  })

  const invoiceAddress = computed({
    get: () => session.orders[deal.id].invoiceAddress,
    set(value: Address) {
      session.orders[deal.id].invoiceAddress = value
    },
  })

  const invoiceRequired = computed({
    get: () => session.orders[deal.id].invoiceRequired,
    set(value: boolean) {
      session.orders[deal.id].invoiceRequired = value
    },
  })

  const invoiceReason = computed({
    get: () => session.orders[deal.id].invoiceReason,
    set(value: string) {
      session.orders[deal.id].invoiceReason = value
    },
  })

  const extraFieldValues = computed({
    get: () => session.orders[deal.id].extraFieldValues,
    set(value: ExtraFieldValueList) {
      session.orders[deal.id].extraFieldValues = value
    },
  })

  const paymentMethodIdentifier = computed({
    get: () => session.orders[deal.id].paymentMethodIdentifier,
    set(value: string) {
      session.orders[deal.id].paymentMethodIdentifier = value
    },
  })

  const shouldCreateMandate = computed({
    get: () => session.orders[deal.id].shouldCreateMandate,
    set(value: boolean) {
      session.orders[deal.id].shouldCreateMandate = value
    },
  })

  const isPending = computed(() => session.orders[deal.id].pending ?? false)

  // order soruce & lcc

  function getOrderSource(): string {
    return session.source ?? globalThis.location.href
  }

  function getOrderLcc(): string {
    let lcc = ''

    try {
      if (longterm.lcc) {
        longterm.lcc.forEach((lccItem: string) => {
          lcc = lccItem.split(':').shift() ?? lccItem
        })

        lcc = lcc.substring(0, 50)
      }
    } catch (error) {
      console.error('Error getting LCC: ', error)
    }

    return lcc
  }

  // create outspot-plus order
  async function createOutspotPlusOrder(componentsCardToken?: string) {
    const plusOrder = await api.plus.create(
      session.orders[deal.id].user.email,
      session.orders[deal.id].user.firstName,
      session.orders[deal.id].user.lastName,
      session.orders[deal.id].user.birthDate,
      getRouteUrl('outspot-plus/order/finished'),
      paymentMethodIdentifier.value,
      componentsCardToken,
      session.outspotPlusLinkToken ?? null,
    )

    plusOrder.data?.user &&
      (await authentication.softAuthenticate(plusOrder.data?.user))

    session.orders[deal.id].pending = true

    return plusOrder
  }

  // create remote order
  async function createRemoteOrder() {
    const order = await api.orders.create(
      session.orders[deal.id].user.email,
      deal.id,
      getOrderSource(),
      getOrderLcc(),
      session.orders[deal.id].overwriteOrderId ?? undefined,
      createRemoteOrderForStep,
    )

    session.orders[deal.id].overwriteOrderId = order.data?.id

    return order
  }

  // finish remote order
  let isFinishingRemoteOrder = false

  async function finishRemoteOrder(componentsCardToken?: string) {
    // prevent double submission
    if (isFinishingRemoteOrder) {
      return
    }

    isFinishingRemoteOrder = true

    // create basic order
    const order = await createRemoteOrder()

    if (order.error) {
      isFinishingRemoteOrder = false
      return order
    }

    // update the user profile
    try {
      if (order.data?.user) {
        await api.user.updateInfo(
          order.data.user.email,
          session.orders[deal.id].user.firstName,
          session.orders[deal.id].user.lastName,
          session.orders[deal.id].user.birthDate,
          session.orders[deal.id].user.phone,
        )
      }
    } catch (ex) {}

    // soft authenticate the user
    order.data?.user && (await authentication.softAuthenticate(order.data.user))

    // add extra fields & invoicing info
    const orderWithExtraFields = await api.orders.update(
      // @ts-ignore
      order.data.id,
      session.orders[deal.id].user.email,
      (session.orders[deal.id].deliveryAddress.firstname ?? null) ||
        session.orders[deal.id].user.firstName,
      (session.orders[deal.id].deliveryAddress.name ?? null) ||
        session.orders[deal.id].user.lastName,
      session.orders[deal.id].user.phone,
      session.orders[deal.id].deliveryAddress,
      session.orders[deal.id].deliveryServicePoint,
      session.orders[deal.id].invoiceAddress,
      session.orders[deal.id].invoiceRequired,
      session.orders[deal.id].invoiceReason,
    )

    if (orderWithExtraFields.error) {
      isFinishingRemoteOrder = false
      return orderWithExtraFields
    }

    // Merge regular and singular options
    const mergedOptions = computed(() => {
      return [...selectedRegularOptions.value, ...selectedSingularOptions.value]
    })

    // add options with extra fields
    const orderDetails = await Promise.all(
      mergedOptions.value.map(async (optionQuantity: OptionQuantity) =>
        api.orders.addOption(
          // @ts-ignore
          order.data.id,
          session.orders[deal.id].user.email,
          optionQuantity.option.id,
          optionQuantity.quantity,
          Array(optionQuantity.quantity)
            .fill(null)
            .map((value, index) => {
              const extraFieldValues: any = {}

              optionQuantity.option.extraFields?.forEach(
                (extraField: ExtraField) => {
                  extraFieldValues[extraField.id] = {
                    id: extraField.id,
                    value:
                      session.orders[deal.id].extraFieldValues[
                        `${optionQuantity.option.id}_${extraField.id}_${
                          index + 1
                        }`
                      ] ?? '',
                  }
                },
              )

              return extraFieldValues
            }),
          getOptionData(optionQuantity.option, null),
        ),
      ),
    )

    // check if all options were added
    if (orderDetails.some((orderDetail: any) => orderDetail.error)) {
      orderWithExtraFields.error = true
      orderWithExtraFields.data = { details: true }
      isFinishingRemoteOrder = false
      return orderWithExtraFields
    }

    // if we need to calculate us tax, stop here
    if (needsTaxes.value) {
      const taxOrder = await api.orders.calculateTaxes(
        order.data!.id,
        session.orders[deal.id].user.email,
      )

      // store taxes in the next tick to make sure the order is not finished right away
      setTimeout(() => {
        if (taxOrder.data?.details) {
          session.orders[deal.id].taxes = taxOrder.data.details.filter(
            (detail: RemoteOrderDetail) => detail.code.endsWith('_TAX'),
          )
        }
      }, 1000)

      isFinishingRemoteOrder = false
      return taxOrder
    }

    // payment method
    let method = paymentMethodIdentifier.value
    let mandate = undefined
    let applePayPaymentToken = undefined

    if (method.startsWith('mdt_')) {
      mandate = method
      method = 'creditcard'
    } else if (method.startsWith('nuvei_mandate:')) {
      mandate = method
      method = 'nuvei:default'
    } else if (applePayPaymentToken) {
      // TODO: apple pay local
    }

    // finish
    const finishedOrder = await api.orders.finish(
      // @ts-ignore
      order.data.id,
      session.orders[deal.id].user.email,
      getDealRouteUrl(deal, 'order/finished'),
      (session.orders[deal.id].coupons ?? []).map(
        (coupon: Coupon) => coupon.code,
      ),
      method,
      mandate,
      applePayPaymentToken,
      componentsCardToken,
      isRecurringPaymentMethodIdentifier(method) &&
        (session.orders[deal.id].shouldCreateMandate ?? false),
    )

    if (finishedOrder.error) {
      isFinishingRemoteOrder = false
      return finishedOrder
    }

    // store the order id
    session.orders[deal.id].remoteOrder = order.data?.id
    session.orders[deal.id].pending = true

    // track
    tracking.trackEvent('salesflow_finish_purchase')

    isFinishingRemoteOrder = false
    return finishedOrder
  }

  const remoteOrder = computed(
    () => session.orders[deal.id].remoteOrder ?? null,
  )

  async function getRemoteOrder() {
    if (!remoteOrder.value) {
      return null
    }

    const order = await api.orders.get(
      remoteOrder.value,
      session.orders[deal.id].user.email,
      false,
    )

    // store taxes
    if (order.data?.value?.details) {
      session.orders[deal.id].taxes = order.data.value.details.filter(
        (detail: RemoteOrderDetail) => detail.code.endsWith('_TAX'),
      )
    }

    // store user info for later
    if (
      order.data?.value?.finished ||
      paymentMethodIdentifier.value === 'bank'
    ) {
      // soft authenticate the user
      order.data?.value?.user &&
        (await authentication.softAuthenticate(order.data.value.user))

      // store user info for later
      longterm.autocomplete = {
        user: session.orders[deal.id].user,
        deliveryAddress: needsShipping.value
          ? session.orders[deal.id].deliveryAddress
          : (longterm.autocomplete?.deliveryAddress ?? null),
        invoiceAddress: session.orders[deal.id].invoiceRequired
          ? session.orders[deal.id].invoiceAddress
          : (longterm.autocomplete?.invoiceAddress ?? null),
        invoiceRequired: session.orders[deal.id].invoiceRequired ?? false,
        invoiceReason: session.orders[deal.id].invoiceReason ?? null,
      }
    }

    return order
  }

  const coupons = computed(() => {
    return (session.orders[deal.id].coupons ?? []).map((coupon: Coupon) => {
      return {
        code: coupon.code,
        amount: coupon.amount,
        cumulative: coupon.cumulative,
        percentage: coupon.percentage,
        calculatedAmount: coupon.percentage
          ? Math.round(totalPayable.value * (coupon.amount / 100) * 100) / 100
          : coupon.amount,
      }
    })
  })

  async function addCoupon(code: string) {
    // create a remote order to validate the coupon against
    const order = await createRemoteOrder()

    if (order.error) {
      throw new Error('incomplete_order')
    }

    // validate the coupon (remote)
    // @ts-ignore
    const coupon = await api.orders.getCoupon(
      order.data!.id,
      code,
      session.orders[deal.id].user.email,
      false,
    )

    if (coupon.error.value === true) {
      throw new Error('invalid_coupon')
    }

    // check if the coupon isn't already added
    if (
      session.orders[deal.id].coupons.find(
        (coupon: Coupon) => coupon.code === code,
      )
    ) {
      throw new Error('coupon_already_added')
    }

    // check if the coupon is cumulative
    if (!coupon.data.value?.cumulative && coupons.value.length > 0) {
      throw new Error('coupon_not_cumulative')
    }

    if (
      coupons.value.filter((coupon: Coupon) => !coupon.cumulative).length > 0
    ) {
      throw new Error('other_coupon_not_cumulative')
    }

    // add the coupon to the local order
    session.orders[deal.id].coupons.push(coupon.data.value)

    // track
    tracking.trackEvent('salesflow_validate_coupon')
  }

  async function deleteCoupon(code: string) {
    session.orders[deal.id].coupons = session.orders[deal.id].coupons.filter(
      (coupon: Coupon) => coupon.code !== code,
    )
    tracking.trackEvent('salesflow_remove_coupon')
  }

  async function refreshUser() {
    try {
      if (!authentication.isAuthenticated()) {
        const order = await createRemoteOrder()
        order.data?.user &&
          (await authentication.softAuthenticate(order.data.user))
      }
    } catch (error) {}
  }

  // return
  return {
    incrementOption: incrementOption,
    decrementOption: decrementOption,
    setOptionQuantity: setOptionQuantity,
    incrementInitialOption: incrementInitialOption,

    setOptionData: setOptionData,
    getOptionData: getOptionData,

    isOptionSelected: isOptionSelected,
    isVariantSelected: isVariantSelected,

    allSelectedOptions: allSelectedOptions,
    selectedFeeOptions: selectedFeeOptions,
    selectedRegularOptions: selectedRegularOptions,
    hasValidOptionSelection: hasValidOptionSelection,
    selectedSingularOptions: selectedSingularOptions,
    hasValidSingularOptionSelection: hasValidSingularOptionSelection,

    hasOpenGdsOptions: hasOpenGdsOptions,

    totalRegularOptionsWithDiscount: totalRegularOptionsWithDiscount,
    totalRegularOptionsOriginal: totalRegularOptionsOriginal,
    totalSingularOptionsWithDiscount: totalSingularOptionsWithDiscount,
    totalFeeOptions: totalFeeOptions,
    totalPayable: totalPayable,
    totalPayableWithCoupons: totalPayableWithCoupons,
    totalDiscount: totalDiscount,
    totalCoupons: totalCoupons,
    totalTaxes: totalTaxes,

    isPending: isPending,
    needsShipping: needsShipping,

    clearTaxes: clearTaxes,
    taxes: taxes,
    needsTaxes: needsTaxes,

    user: user,

    deliveryAddress: deliveryAddress,
    deliveryServicePoint: deliveryServicePoint,

    invoiceAddress: invoiceAddress,
    invoiceRequired: invoiceRequired,
    invoiceReason: invoiceReason,

    extraFieldValues: extraFieldValues,

    paymentMethodIdentifier: paymentMethodIdentifier,
    shouldCreateMandate: shouldCreateMandate,

    finishRemoteOrder: finishRemoteOrder,
    remoteOrder: remoteOrder,
    getRemoteOrder: getRemoteOrder,

    createOutspotPlusOrder: createOutspotPlusOrder,

    addCoupon: addCoupon,
    deleteCoupon: deleteCoupon,
    coupons: coupons,

    refreshUser: refreshUser,
  }
}
